import { useState, useEffect, useCallback, useRef } from 'react'
import { SupabaseClient } from '@supabase/supabase-js'
import { OrgMetrics } from './types'

const DEFAULT_METRICS: OrgMetrics = {
  productsCount: 0,
  brandsCount: 0,
  usersCount: 0,
  reviewsCount: 0,
  scansCount: 0,
  premiumUsers: 0,
  premiumMonthly: 0,
  premiumYearly: 0,
}

// Local cache for instant page loads
const STORAGE_KEY = 'nexus_org_metrics_cache'
const LOCAL_CACHE_TTL_MS = 5 * 60 * 1000 // 5 minutes

interface CachedMetrics {
  metrics: OrgMetrics
  timestamp: number
}

function getLocalCache(): CachedMetrics | null {
  try {
    const cached = localStorage.getItem(STORAGE_KEY)
    if (cached) {
      const parsed = JSON.parse(cached) as CachedMetrics
      if (Date.now() - parsed.timestamp < LOCAL_CACHE_TTL_MS) {
        return parsed
      }
    }
  } catch (e) {
    console.warn('Failed to read org metrics cache:', e)
  }
  return null
}

function setLocalCache(metrics: OrgMetrics): void {
  try {
    const cached: CachedMetrics = { metrics, timestamp: Date.now() }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cached))
  } catch (e) {
    console.warn('Failed to write org metrics cache:', e)
  }
}

export function useOrgMetrics(client: SupabaseClient | null) {
  // Initialize with cached data for instant load
  const [metrics, setMetrics] = useState<OrgMetrics>(() => {
    const cached = getLocalCache()
    return cached?.metrics || DEFAULT_METRICS
  })
  const [isLoading, setIsLoading] = useState(() => !getLocalCache())
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const isMounted = useRef(true)

  const fetchMetrics = useCallback(async (forceRefresh = false) => {
    if (!client) {
      setIsLoading(false)
      return
    }

    // Check local cache first (unless forcing refresh)
    if (!forceRefresh) {
      const cached = getLocalCache()
      if (cached?.metrics) {
        setMetrics(cached.metrics)
        setIsLoading(false)

        // If cache is fresh, just use it
        const cacheAge = Date.now() - cached.timestamp
        if (cacheAge < LOCAL_CACHE_TTL_MS) {
          console.log(`OrgMetrics: Using cached data (${Math.round(cacheAge / 1000)}s old)`)
          return
        }
        // Cache is stale, refresh in background
        console.log('OrgMetrics: Cache stale, refreshing in background...')
      }
    }

    // Show loading only if we don't have data yet
    const hasData = metrics.productsCount > 0 || metrics.brandsCount > 0
    if (hasData) {
      setIsRefreshing(true)
    } else {
      setIsLoading(true)
    }
    setError(null)

    try {
      // Run all counts in parallel
      const [
        { count: productsCount },
        { count: brandsCount },
        { count: usersCount },
        { count: reviewsCount },
        { count: scansCount },
        { count: premiumUsers },
        { count: premiumMonthly },
        { count: premiumYearly },
      ] = await Promise.all([
        client.from('products').select('*', { count: 'exact', head: true }),
        client.from('brands').select('*', { count: 'exact', head: true }),
        client.from('users').select('*', { count: 'exact', head: true }),
        client.from('product_reviews').select('*', { count: 'exact', head: true }),
        client.from('recent_scans').select('*', { count: 'exact', head: true }),
        client.from('users').select('*', { count: 'exact', head: true }).eq('subscription_tier', 'premium'),
        client.from('users').select('*', { count: 'exact', head: true }).eq('subscription_tier', 'premium').eq('billing_period', 'monthly'),
        client.from('users').select('*', { count: 'exact', head: true }).eq('subscription_tier', 'premium').eq('billing_period', 'yearly'),
      ])

      const newMetrics: OrgMetrics = {
        productsCount: productsCount || 0,
        brandsCount: brandsCount || 0,
        usersCount: usersCount || 0,
        reviewsCount: reviewsCount || 0,
        scansCount: scansCount || 0,
        premiumUsers: premiumUsers || 0,
        premiumMonthly: premiumMonthly || 0,
        premiumYearly: premiumYearly || 0,
      }

      if (isMounted.current) {
        setMetrics(newMetrics)
        setLocalCache(newMetrics)
      }
    } catch (err) {
      console.error('Error fetching org metrics:', err)
      if (isMounted.current && !hasData) {
        setError('Failed to fetch metrics')
      }
    } finally {
      if (isMounted.current) {
        setIsLoading(false)
        setIsRefreshing(false)
      }
    }
  }, [client, metrics])

  useEffect(() => {
    isMounted.current = true
    fetchMetrics()
    return () => { isMounted.current = false }
  }, [client])

  return { metrics, isLoading, isRefreshing, error, refetch: fetchMetrics }
}
